import './pagina-editor-modal.js';
import type { Pagina } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgChart, PgKpiCard } from '@pgcustom/design-system';
import type { CmsApi } from './cms.api.js';
import type { PermissoesCms } from './cms.permissoes.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import type { ErpPaginaEditorModal } from './pagina-editor-modal.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'titulo', label: 'Página' },
  { key: 'status', label: 'Status' },
  { key: 'publicadoEm', label: 'Última publicação' },
];

/**
 * `<erp-cms-page>` — CMS do site (Sprint 11, `ROADMAP.md`: "editar o
 * site sem tocar em código"). Conjunto FIXO de 6 páginas (migration
 * `017`) — sem criação/exclusão, só edição via clique de linha, que
 * abre `<erp-pagina-editor-modal>`. Ferramenta ADMINISTRATIVA apenas:
 * não renderiza nem publica no site público de verdade
 * (`pgcustom.art.br` é um projeto separado, fora deste monorepo —
 * `docs/PROJECT_STATE.md`, seção 27, item 19).
 */
export class ErpCmsPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _cmsApi: CmsApi | null = null;
  private _permissoes: PermissoesCms = { ler: false, atualizar: false };

  private resultadoPaginas: Resultado<Pagina[]> = { status: 'carregando' };
  private paginasAtuais: Pagina[] = [];

  private modalEditor!: ErpPaginaEditorModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set cmsApi(valor: CmsApi) {
    this._cmsApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesCms) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._cmsApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .dica { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: var(--pg-space-3, 12px); }
        .layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(220px, 280px); gap: var(--pg-space-4, 16px); align-items: start; margin-bottom: var(--pg-space-5, 24px); }
        @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
      </style>
      <div class="topo">
        <div>
          <h1>CMS do Site</h1>
          <div class="subtitulo">Gestão administrativa das páginas do site — não publica no site público</div>
        </div>
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-total" label="Total de páginas" icone="cms" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-publicadas" label="Publicadas" icone="cms" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-pendentes" label="Com alterações pendentes" icone="cms" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-nunca" label="Nunca publicadas" icone="cms" estado="carregando"></pg-kpi-card>
      </div>

      <div class="layout">
        <pg-card padding="compacto">
          <div id="paginas"></div>
        </pg-card>
        <pg-card padding="compacto">
          <span slot="header">Status</span>
          <div id="donut-status"><pg-empty-state variant="carregando" titulo="Status"></pg-empty-state></div>
        </pg-card>
      </div>
      ${this._permissoes.atualizar ? '<p class="dica">Clique em uma página para editar rascunho, publicar ou restaurar uma versão anterior.</p>' : ''}

      <erp-pagina-editor-modal id="modal-editor"></erp-pagina-editor-modal>
    `;

    this.modalEditor = this.root.querySelector('#modal-editor') as ErpPaginaEditorModal;
    if (this._cmsApi) this.modalEditor.cmsApi = this._cmsApi;
    this.modalEditor.permissoes = { atualizar: this._permissoes.atualizar };
    this.modalEditor.aoSalvar = () => void this.carregar();

    this.root.querySelector('#paginas')?.addEventListener('pg-row-click', (evento) => {
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const pagina = this.paginasAtuais[index];
      if (pagina) void this.modalEditor.abrir(pagina);
    });

    this.atualizar();
  }

  private async carregar(): Promise<void> {
    const api = this._cmsApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoPaginas = { status: 'sem-permissao' };
      this.atualizar();
      return;
    }

    this.resultadoPaginas = { status: 'carregando' };
    this.atualizar();
    this.resultadoPaginas = await api.listar();
    this.atualizar();
  }

  private atualizar(): void {
    const container = this.root.querySelector('#paginas');
    if (!container) return;

    if (this.resultadoPaginas.status === 'ok') {
      this.paginasAtuais = this.resultadoPaginas.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.paginasAtuais.map((p) => ({
        titulo: p.titulo,
        status: estadoPagina(p),
        publicadoEm: p.publicadoEm ? formatarDataHora(p.publicadoEm) : '—',
      }));
      this.atualizarResumo(this.paginasAtuais);
      return;
    }

    this.paginasAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoPaginas);
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-total', 'kpi-publicadas', 'kpi-pendentes', 'kpi-nunca']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoPaginas.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
    const donut = this.root.querySelector('#donut-status');
    if (donut) donut.innerHTML = `<pg-empty-state variant="${variant}" titulo="Status" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarResumo(paginas: Pagina[]): void {
    const publicadas = paginas.filter((p) => p.conteudoPublicado && !p.possuiAlteracoesPendentes).length;
    const pendentes = paginas.filter((p) => p.conteudoPublicado && p.possuiAlteracoesPendentes).length;
    const nunca = paginas.filter((p) => !p.conteudoPublicado).length;

    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('valor', String(paginas.length));
    (this.root.querySelector('#kpi-publicadas') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-publicadas') as PgKpiCard | null)?.setAttribute('valor', String(publicadas));
    (this.root.querySelector('#kpi-pendentes') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-pendentes') as PgKpiCard | null)?.setAttribute('valor', String(pendentes));
    (this.root.querySelector('#kpi-nunca') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-nunca') as PgKpiCard | null)?.setAttribute('valor', String(nunca));

    const donut = this.root.querySelector('#donut-status');
    if (!donut) return;
    donut.innerHTML = '<pg-chart></pg-chart>';
    const chart = donut.querySelector('pg-chart') as PgChart;
    chart.type = 'donut';
    chart.series = [{ key: 'valor', label: '' }];
    chart.points = [
      { label: 'Publicada', values: { valor: publicadas }, color: 'var(--pg-color-sucesso)' },
      { label: 'Alterações pendentes', values: { valor: pendentes }, color: 'var(--pg-color-alerta)' },
      { label: 'Nunca publicada', values: { valor: nunca }, color: 'var(--pg-color-texto-secundario)' },
    ];
    chart.formatarValor = (valor) => String(valor);
  }
}

function estadoPagina(p: Pagina): { texto: string; tom: 'sucesso' | 'alerta' | 'neutro' } {
  if (!p.conteudoPublicado) return { texto: 'Nunca publicada', tom: 'neutro' };
  if (p.possuiAlteracoesPendentes) return { texto: 'Publicada (alterações pendentes)', tom: 'alerta' };
  return { texto: 'Publicada', tom: 'sucesso' };
}

function estadoVazio(resultado: Resultado<Pagina[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'CMS do Site', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'CMS do Site', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'CMS do Site', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhuma página', descricao: 'nenhuma página cadastrada' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-cms-page', ErpCmsPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-cms-page': ErpCmsPage;
  }
}
